import { Form, Input, Modal, Tooltip } from 'antd'
import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { TbMessage2Star } from 'react-icons/tb'

import { useNotification } from '@/Core/Hooks/useNotification'
import { Button } from '@/Core/UI/Button'
import { favoritesManager } from '@/Ship/Stores/FavoritesManager'

import { favoriteTitle } from './FavoriteToggle'

import type { FavoriteCardContext } from './FavoriteToggle'

/** Бек тримає коментар до 4096 символів (`UserFavoriteEntity::$comment`). */
export const FAVORITE_COMMENT_MAX = 4096

const LABEL = 'Додати в обране з коментарем'

interface FavoriteCommentButtonProps {
    resourceKey: string
    crudContext: FavoriteCardContext
}

/**
 * «Додати в обране з коментарем» — друга кнопка поруч із зірочкою
 * (`SPEC-014` §4.3, ADR 029).
 *
 * Видна, лише поки запис **не** в обраному, як у легасі: коментар — це спосіб
 * позначити, а не окрема дія над уже позначеним.
 *
 * ⚠️ Модалка не закривається, доки коментар не пройшов перевірку й сервер не
 * зберіг позначку. У легасі `onOk` закривав її за будь-якого результату, і
 * порожній коментар мовчки нічого не зберігав.
 */
export const FavoriteCommentButton = observer(({ resourceKey, crudContext }: FavoriteCommentButtonProps) => {
    const { showError } = useNotification()
    const [form] = Form.useForm<{ comment: string }>()
    const [isOpen, setIsOpen] = useState(false)
    const [isSaving, setIsSaving] = useState(false)

    const values = crudContext.formContext?.values ?? {}
    const uuid = typeof values['uuid'] === 'string' ? values['uuid'] : null

    if (!uuid || !favoritesManager.isLoaded || favoritesManager.find(resourceKey, uuid)) return null

    const close = () => {
        setIsOpen(false)
        form.resetFields()
    }

    const save = async ({ comment }: { comment: string }) => {
        setIsSaving(true)

        try {
            await favoritesManager.add(resourceKey, uuid, favoriteTitle(resourceKey, values), comment)
            close()
        } catch {
            showError('Не вдалося додати до обраного')
        } finally {
            setIsSaving(false)
        }
    }

    return (
        <>
            <Tooltip title={LABEL} placement="bottom">
                <span style={{ display: 'inline-flex' }}>
                    <Button
                        className="button-group-item"
                        size="middle"
                        aria-label={LABEL}
                        icon={<TbMessage2Star style={{ fontSize: 18 }} />}
                        disabled={favoritesManager.isPending(resourceKey, uuid)}
                        onClick={() => setIsOpen(true)}
                    />
                </span>
            </Tooltip>

            <Modal
                title={LABEL}
                open={isOpen}
                okText="Зберегти"
                cancelText="Скасувати"
                confirmLoading={isSaving}
                destroyOnHidden
                onOk={() => form.submit()}
                onCancel={close}
            >
                <Form form={form} layout="vertical" onFinish={(data) => void save(data)}>
                    <Form.Item
                        name="comment"
                        label="Коментар"
                        rules={[
                            { required: true, whitespace: true, message: 'Введіть коментар' },
                            { max: FAVORITE_COMMENT_MAX, message: `Не більше ${FAVORITE_COMMENT_MAX} символів` },
                        ]}
                    >
                        <Input.TextArea autoFocus autoSize={{ minRows: 3, maxRows: 8 }} maxLength={FAVORITE_COMMENT_MAX} />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    )
})

FavoriteCommentButton.displayName = 'FavoriteCommentButton'
